// Kết nối WS /ui: tự nối lại lùi luỹ thừa 1 → 30 s, phát sự kiện cho trang, báo trạng thái (để hiện chấm "mất kết nối realtime").
import type { UiEvent } from "./types";

type Handler = (ev: UiEvent) => void;
type StateHandler = (connected: boolean) => void;

const RECONNECT_MIN_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

class UiSocket {
  private ws: WebSocket | null = null;
  private handlers = new Set<Handler>();
  private stateHandlers = new Set<StateHandler>();
  private backoff = RECONNECT_MIN_MS;
  private timer: number | undefined;
  private wanted = false;
  private pingTimer: number | undefined;
  connected = false;

  start(): void {
    this.wanted = true;
    this.open();
  }

  stop(): void {
    this.wanted = false;
    clearTimeout(this.timer);
    clearInterval(this.pingTimer);
    this.ws?.close();
    this.ws = null;
    this.setConnected(false);
  }

  on(fn: Handler): () => void {
    this.handlers.add(fn);
    return () => this.handlers.delete(fn);
  }

  onState(fn: StateHandler): () => void {
    this.stateHandlers.add(fn);
    fn(this.connected);
    return () => this.stateHandlers.delete(fn);
  }

  /** Phát sự kiện nội bộ cho các trang (không qua mạng) — dùng sau khi sửa/xoá để giao diện đổi ngay. */
  emitLocal(ev: UiEvent): void {
    for (const f of this.handlers) {
      try { f(ev); } catch (e) { console.error("[ws] handler", e); }
    }
  }

  send(msg: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  /** Bật/tắt xem trực tiếp một bot (server đếm người xem theo kết nối → view.start/stop tới bot). */
  watch(botId: string, on: boolean): void {
    this.send({ type: "watch", botId, on });
  }

  private setConnected(v: boolean): void {
    if (this.connected === v) return;
    this.connected = v;
    for (const f of this.stateHandlers) f(v);
  }

  private open(): void {
    if (!this.wanted) return;
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    let ws: WebSocket;
    try {
      ws = new WebSocket(`${proto}//${location.host}/ui`);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.backoff = RECONNECT_MIN_MS;
      this.setConnected(true);
      clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => this.send({ type: "ping" }), 25_000);
    };
    ws.onmessage = (m) => {
      let ev: UiEvent;
      try {
        ev = JSON.parse(String(m.data)) as UiEvent;
      } catch {
        return;
      }
      if (!ev || typeof ev !== "object" || typeof (ev as { type?: unknown }).type !== "string") return;
      for (const f of this.handlers) {
        try { f(ev); } catch (e) { console.error("[ws] handler", e); }
      }
    };
    ws.onclose = () => {
      if (this.ws === ws) this.ws = null;
      clearInterval(this.pingTimer);
      this.setConnected(false);
      this.scheduleReconnect();
    };
    ws.onerror = () => { /* onclose sẽ chạy ngay sau */ };
  }

  private scheduleReconnect(): void {
    if (!this.wanted) return;
    clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.open(), this.backoff);
    this.backoff = Math.min(RECONNECT_MAX_MS, this.backoff * 2);
  }
}

export const uiSocket = new UiSocket();
